import React, { createContext, useState, useEffect, useCallback } from 'react';

export const ThemeContext = createContext({
    theme: 'light',
    toggleTheme: () => {}
});

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => {
        // Check local storage first
        const savedTheme = localStorage.getItem('theme');

        // If no saved theme, check system preference
        if (!savedTheme) {
            return window.matchMedia('(prefers-color-scheme: dark)').matches
                ? 'dark'
                : 'light';
        }

        return savedTheme;
    });

    // Memoized toggle function
    const toggleTheme = useCallback(() => {
        setTheme((prevTheme) => {
            const newTheme = prevTheme === 'light' ? 'dark' : 'light';

            // Update local storage
            localStorage.setItem('theme', newTheme);

            // Update document attribute
            document.documentElement.setAttribute('data-theme', newTheme);

            return newTheme;
        });
    }, []);

    // Effect to handle initial theme setup
    useEffect(() => {
        // Set initial theme on document
        document.documentElement.setAttribute('data-theme', theme);

        // Handle system theme change
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        const handleThemeChange = (e) => {
            const systemTheme = e.matches ? 'dark' : 'light';

            // Only change if no user preference is saved
            if (!localStorage.getItem('theme')) {
                setTheme(systemTheme);
                document.documentElement.setAttribute('data-theme', systemTheme);
            }
        };

        // Add listener for system theme changes
        mediaQuery.addListener(handleThemeChange);

        // Cleanup
        return () => {
            mediaQuery.removeListener(handleThemeChange);
        };
    }, [theme]);

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};